import { axiosStudentInstance } from "./studentAuth"
import type { AssessmentFeed } from "../page/Student/AiAssessor/types"
import type {
  Attempt,
  PaginationMeta,
  QuestionListFilter,
  StudentQuestionDetail,
  StudentQuestionListItem,
} from "../page/Student/Questions/types"

export interface StudentApiEnvelope<T> {
  success: boolean
  status_code: number
  message: string
  data: T
  pagination?: PaginationMeta
}

const cleanParams = (filter: QuestionListFilter) => {
  const params: Record<string, string | number | string[]> = {
    page_number: filter.page_number,
    per_page: filter.per_page,
    sort_by: filter.sort_by,
    sort_order: filter.sort_order,
  }
  if (filter.subject?.trim()) {
    params.subject = filter.subject.trim()
  }
  const hierarchyIds = (filter.hierarchy_ids || [])
    .map((id) => String(id).trim())
    .filter(Boolean)
  if (hierarchyIds.length > 0) {
    params.hierarchy_ids = hierarchyIds
  } else {
    ;(["level_1", "level_2", "level_3", "level_4"] as const).forEach((key) => {
      const value = filter[key]
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        params[key] = String(value).trim()
      }
    })
  }
  ;(["keyword", "paper", "complexity"] as const).forEach((key) => {
    const value = filter[key]
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params[key] = String(value).trim()
    }
  })
  if (filter.year !== undefined && filter.year !== null && String(filter.year).trim() !== "") {
    const yearNum = Number(filter.year)
    if (!Number.isNaN(yearNum)) params.year = yearNum
  }
  return params
}

/** Repeat array keys as hierarchy_ids=a&hierarchy_ids=b for FastAPI List[str]. */
const paramsSerializer = (params: Record<string, unknown>): string => {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null || String(item) === "") return
        search.append(key, String(item))
      })
      return
    }
    search.append(key, String(value))
  })
  return search.toString()
}

export const listStudentQuestions = (filter: QuestionListFilter) =>
  axiosStudentInstance.get<StudentApiEnvelope<StudentQuestionListItem[]>>(
    "/questions/",
    { params: cleanParams(filter), paramsSerializer }
  )

export const getStudentQuestionDetail = (questionId: string) =>
  axiosStudentInstance.get<StudentApiEnvelope<StudentQuestionDetail>>(
    `/questions/${questionId}/`
  )

export const listStudentBookmarks = (page_number = 1, per_page = 10) =>
  axiosStudentInstance.get<StudentApiEnvelope<StudentQuestionListItem[]>>(
    "/bookmarks/",
    { params: { page_number, per_page } }
  )

export const bookmarkStudentQuestion = (questionId: string) =>
  axiosStudentInstance.post(`/questions/${questionId}/bookmark/`)

export const unbookmarkStudentQuestion = (questionId: string) =>
  axiosStudentInstance.delete(`/questions/${questionId}/bookmark/`)

export const saveStudentQuestionNote = (questionId: string, notes: string) =>
  axiosStudentInstance.put(`/questions/${questionId}/notes/`, { notes })

export const deleteStudentQuestionNote = (questionId: string) =>
  axiosStudentInstance.delete(`/questions/${questionId}/notes/`)

export const saveStudentQuestionTags = (questionId: string, tags: string[]) =>
  axiosStudentInstance.put(`/questions/${questionId}/tags/`, { tags })

export const addStudentQuestionTag = (questionId: string, tag: string) =>
  axiosStudentInstance.post<
    StudentApiEnvelope<{
      question_id: string
      tags: string[]
      state_changed?: boolean
    }>
  >(`/questions/${questionId}/tags/`, { tag })

export const removeStudentQuestionTag = (questionId: string, tag: string) =>
  axiosStudentInstance.delete<
    StudentApiEnvelope<{
      question_id: string
      tags: string[]
      state_changed?: boolean
    }>
  >(`/questions/${questionId}/tags/`, {
    params: { tag },
  })

export const listStudentQuestionAttempts = (questionId: string) =>
  axiosStudentInstance.get<StudentApiEnvelope<Attempt[]>>(
    `/questions/${questionId}/attempts/`
  )

export const submitStudentQuestionAttempt = (
  questionId: string,
  body: { answerText: string; timeSpentSeconds?: number }
) =>
  axiosStudentInstance.post<StudentApiEnvelope<Attempt>>(
    `/questions/${questionId}/attempts/`,
    body
  )

export const listStudentAssessments = (params: {
  kind: "all" | "questions" | "papers"
  subject?: string
  topic?: string
  sort: "newest" | "best" | "worst"
  page_number: number
  per_page: number
}) =>
  axiosStudentInstance.get<StudentApiEnvelope<AssessmentFeed>>("/assessments/", {
    params,
  })
